export interface TopicSection {
  heading: string;
  paragraphs: string[];
}

export interface TopicTimelineItem {
  time: string;
  event: string;
}

export interface Topic {
  slug: string;
  title: string;
  category: string;
  summary: string;
  tags: string[];
  date: string;
  readTime: number;
  heat: number;
  cover: string;
  featured?: boolean;
  keyPoints: string[];
  sections: TopicSection[];
  timeline: TopicTimelineItem[];
}

export const categories = [
  { key: '行业内幕', desc: '拆解行业里不轻易被说破的运作方式' },
  { key: '消费乱象', desc: '记录消费场景中反复出现的套路与陷阱' },
  { key: '维权纠纷', desc: '梳理围绕售后、退款与责任的真实争议' },
  { key: '争议事件', desc: '呈现多方说法尚未达成一致的公共事件' },
];

export const topics: Topic[] = [
  {
    slug: 'livestream-false-promotion',
    title: '直播间"全网最低价"是怎么来的：一场关于价格话术的盘点',
    category: '消费乱象',
    summary:
      '从库存紧张到限时秒杀，直播间里的价格叙事往往被精心编排。我们梳理了几个常见的话术结构，帮你看懂"最低价"背后的计算方式。',
    tags: ['直播带货', '价格误导', '消费提示'],
    date: '2026-08-28',
    readTime: 8,
    heat: 9820,
    cover:
      'https://readdy.ai/api/search-image?query=A%20livestream%20selling%20setup%20with%20ring%20light%20and%20smartphone%20on%20a%20desk%20in%20a%20cozy%20studio%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-01&orientation=landscape',
    featured: true,
    keyPoints: [
      '直播间常见的"原价划线"往往不是真实成交价，而是为对比而设置的参考值。',
      '库存倒计时、限量赠送等营造紧迫感的手法，容易压缩消费者的判断时间。',
      '下单前把商品链接放入多个平台比价，是最简单也最有效的避坑方式。',
    ],
    sections: [
      {
        heading: '价格叙事为什么总能奏效',
        paragraphs: [
          '直播间是一个被高度压缩的决策场。主播需要在几十秒内完成一次转化，于是价格就成了最直接的钩子。划线价、倒计时、库存提示，本质上都是把"现在不买就亏了"这个判断塞进观众的脑海里。',
          '问题在于，这些价格参照并不总是可靠的。把某个从未真实成交过的数字标为"原价"，或用临时抬价再打折的方式制造折扣感，都属于让消费者产生错误价格认知的做法。',
        ],
      },
      {
        heading: '几种常见的话术结构',
        paragraphs: [
          '第一种是"锚定式对比"，先抛出一个极高的参考价，再给出一个看起来划算很多的到手价。第二种是"稀缺式催促"，反复强调库存只剩最后几件，用紧张感替代理性比较。',
          '第三种是"赠品替代降价"，主商品价格不动，但通过叠加赠品让整体看起来更值。这三种结构本身不违法，但当它们被用来传递不实信息时，就越过了边界。',
        ],
      },
      {
        heading: '普通人可以怎么做',
        paragraphs: [
          '最实用的方法其实很朴素：把商品链接丢进比价工具，或者直接在多个平台搜同款，看历史价格曲线。其次是保留直播回放和下单截图，一旦发现宣传与实际不符，这些都能成为后续沟通的依据。',
          '如果确实遇到虚假宣传，可以先向平台投诉，要求平台提供主播与商家的真实信息；协商不成，再通过消费者投诉渠道反映。',
        ],
      },
    ],
    timeline: [
      { time: '第 1 周', event: '多场直播被指出划线价与实际成交价不符，相关内容在社交平台传播。' },
      { time: '第 2 周', event: '平台回应称将加强价格标识审核，部分商品页面补充了价格说明。' },
      { time: '第 3 周', event: '多家机构发布消费提示，建议消费者下单前进行跨平台比价。' },
      { time: '持续追踪', event: '相关规则细化与执行情况仍在跟进。' },
    ],
  },
  {
    slug: 'prepaid-card-refund',
    title: '预付卡"充值容易退款难"：门店经营变化下的余额去向',
    category: '消费乱象',
    summary:
      '健身、美容、洗车等行业的预付卡退款问题反复出现。当门店转让或停止经营，卡里的余额到底该找谁要，我们整理了目前常见的几种情形。',
    tags: ['预付卡', '退款', '消费提示'],
    date: '2026-08-24',
    readTime: 7,
    heat: 8640,
    cover:
      'https://readdy.ai/api/search-image?query=An%20empty%20fitness%20gym%20at%20dawn%20with%20treadmills%20and%20large%20windows%2C%20quiet%20abandoned%20mood%2C%20warm%20muted%20cinematic%20tones%2C%20documentary%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-02&orientation=landscape',
    keyPoints: [
      '预付卡的法律性质是"先付款后消费"，商家未按约定提供服务时消费者有权主张退款。',
      '门店转让并不当然免除原经营者的责任，需要看合同与主体变更情况。',
      '充值前保留合同、转账记录与卡号信息，是后续主张权益的基础。',
    ],
    sections: [
      {
        heading: '预付卡为什么容易出问题',
        paragraphs: [
          '预付卡本质上是消费者先把钱交给商家，商家再在后续一段时间内分批提供服务。这种模式对商家来说是稳定的现金流，对消费者来说却把风险前置了。',
          '一旦门店客流下滑、转让或者直接停止经营，尚未消费的余额就成了一件麻烦事。很多人直到去消费才发现门锁着，此时再想追回款项，难度明显增加。',
        ],
      },
      {
        heading: '三种常见情形怎么处理',
        paragraphs: [
          '第一种是门店正常经营但拒绝退款。此时可以先依据合同条款与商家协商，协商不成向消费者投诉渠道反映，必要时通过诉讼主张。',
          '第二种是门店转让给新经营者。能否向新经营者主张，取决于转让时是否承接了原有会员权益。如果新经营者明确表示不承接，责任通常仍由原经营者承担。',
          '第三种是经营者失去联系。这种情况下需要尽快固定证据，包括合同、付款凭证、门店照片和沟通记录，为后续追索做准备。',
        ],
      },
      {
        heading: '充值之前能做的准备',
        paragraphs: [
          '尽量选择金额较小的套餐，缩短资金在商家手里的时间。签署合同时注意查看主体名称是否与收款方一致，保留纸质或电子合同。',
          '对于明显以"预存送大额"来吸引大额充值的活动，保持谨慎。优惠越大，往往意味着资金占用越久，风险也越集中。',
        ],
      },
    ],
    timeline: [
      { time: '近期', event: '多起预付卡退款纠纷被集中反映，涉及健身、美容等门店。' },
      { time: '跟进中', event: '部分品牌方就门店会员权益承接问题作出说明。' },
      { time: '持续追踪', event: '预付式消费的合同规范与资金监管讨论仍在进行。' },
    ],
  },
  {
    slug: 'rent-loan-dispute',
    title: '长租公寓的"租金贷"：租客、房东与平台之间的三角关系',
    category: '维权纠纷',
    summary:
      '当租金被包装成贷款，租客在不知情中背上了债务。我们梳理了这类纠纷中三方各自的位置，以及租客可以如何降低自身风险。',
    tags: ['长租公寓', '租金贷', '合同纠纷'],
    date: '2026-08-20',
    readTime: 9,
    heat: 7910,
    cover:
      'https://readdy.ai/api/search-image?query=A%20stack%20of%20rental%20lease%20documents%20and%20house%20keys%20on%20a%20wooden%20table%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20window%20light%2C%20clean%20minimal%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-03&orientation=landscape',
    keyPoints: [
      '"租金贷"把租赁关系与借贷关系绑在一起，租客往往直到收到还款提醒才察觉。',
      '签署租赁合同时，要特别留意是否夹带了分期或贷款相关条款。',
      '一旦平台出现经营问题，租客与房东都需要通过正规渠道厘清责任。',
    ],
    sections: [
      {
        heading: '一笔租金背后的两份合同',
        paragraphs: [
          '正常的租赁关系里，租客按期向房东或平台支付租金即可。但在部分长租模式中，平台会引导租客一次性签署一份分期协议，由金融机构先把整年租金付给平台，租客再按月向金融机构还款。',
          '对租客来说，感受上和"按月交租"差不多，但法律关系已经不同：一旦平台把钱挪作他用，租客仍要面对还款义务，而房东也可能因为没收到钱而要求腾房。',
        ],
      },
      {
        heading: '签约时容易忽略的细节',
        paragraphs: [
          '一是签约主体。合同上的出租方、收款方、还款对象可能分属不同公司，需要逐一对清楚。二是付款方式。如果对方要求下载某个金融类应用完成签约，就要格外警惕。',
          '三是违约责任。部分合同会把提前退租与贷款提前结清绑定在一起，导致退租成本远高于预期。',
        ],
      },
      {
        heading: '如何降低自身风险',
        paragraphs: [
          '优先选择按月或按季支付的房源，避免一次性绑定长期资金。仔细阅读每一份需要签字的文件，尤其是带有"分期""借款""服务费"字样的部分。',
          '如果已经卷入纠纷，先保存好合同、转账与还款记录，再分别与平台、房东和金融机构沟通，必要时向监管部门或消费者组织寻求帮助。',
        ],
      },
    ],
    timeline: [
      { time: '阶段一', event: '部分长租平台资金链承压，租金去向引发关注。' },
      { time: '阶段二', event: '租客收到金融还款提醒，纠纷逐渐浮出水面。' },
      { time: '阶段三', event: '相关方面就债务与租赁关系如何拆分展开讨论。' },
      { time: '持续追踪', event: '后续处理方案仍在推进中。' },
    ],
  },
  {
    slug: 'education-refund',
    title: '教培退费拉锯战：课程未上完，钱该怎么退',
    category: '维权纠纷',
    summary:
      '课程调整、机构搬迁或学员个人原因，都会引发退费争议。我们把常见的计算方式与争议焦点整理出来，供有需要的人参考。',
    tags: ['教育培训', '退费', '协商'],
    date: '2026-08-16',
    readTime: 6,
    heat: 6580,
    cover:
      'https://readdy.ai/api/search-image?query=An%20empty%20classroom%20with%20wooden%20desks%20and%20green%20chalkboard%20in%20quiet%20afternoon%2C%20warm%20muted%20tones%2C%20documentary%20editorial%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-04&orientation=landscape',
    keyPoints: [
      '退费金额通常按已消耗课时与剩余课时折算，具体以合同约定为准。',
      '机构单方面变更课程安排时，学员往往有更强的协商立场。',
      '沟通全程尽量使用可留存的文字方式，避免只靠口头承诺。',
    ],
    sections: [
      {
        heading: '退费争议通常卡在哪里',
        paragraphs: [
          '争议焦点大多集中在两处：一是"已消耗"如何认定，二是"赠送课时"是否计入总额。机构一方倾向于把赠课也算进已使用部分，学员一方则认为赠课本就不该计费。',
          '此外，部分合同里会写明"开课后不予退款"或设置高额手续费，这类条款的效力需要结合具体情形判断，并非机构说了算。',
        ],
      },
      {
        heading: '协商时的几个要点',
        paragraphs: [
          '先明确诉求：是全额退、按比例退，还是转课。诉求越清晰，协商越容易推进。其次是把所有沟通落到文字上，聊天记录、邮件都可以作为依据。',
          '如果机构提出分期退款，务必要求写明每期金额与时间，并保留书面确认。',
        ],
      },
      {
        heading: '谈不拢怎么办',
        paragraphs: [
          '协商不成时，可以向消费者投诉渠道或行业主管部门反映，并附上合同与付款凭证。涉及金额较大的，可以考虑通过法律途径主张。',
        ],
      },
    ],
    timeline: [
      { time: '起因', event: '课程安排调整或机构搬迁，引发学员退费诉求。' },
      { time: '协商', event: '双方就折算方式与退费金额反复沟通。' },
      { time: '持续追踪', event: '部分个案进入调解或诉讼程序。' },
    ],
  },
  {
    slug: 'takeaway-fake-orders',
    title: '外卖"刷单"与好评生意：评分究竟有多少水分',
    category: '行业内幕',
    summary:
      '评价体系本是为了帮助消费者做判断，但当评价可以被制造时，它就失去了原本的意义。我们拆解了刷单链条的常见环节。',
    tags: ['外卖', '虚假评价', '平台治理'],
    date: '2026-08-12',
    readTime: 7,
    heat: 6120,
    cover:
      'https://readdy.ai/api/search-image?query=Takeaway%20food%20delivery%20insulated%20bags%20lined%20up%20on%20a%20scooter%20seat%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-05&orientation=landscape',
    keyPoints: [
      '刷单通常通过虚假下单、空包物流与批量好评完成闭环。',
      '评分虚高会让认真做产品的商家反而处于劣势。',
      '看评价时，关注带图与具体描述的长评，比看总分更有参考价值。',
    ],
    sections: [
      {
        heading: '一条刷单链条是如何运转的',
        paragraphs: [
          '刷单并不是简单地找人点五星。较完整的链条会包含虚假下单、配合的物流信息、以及在一定时间内批量出现的好评。这样做的目的，是让平台的风控系统难以从单一维度识别异常。',
          '随着平台规则收紧，刷单手法也在变化，出现了返现引导好评、把差评集中到某个时间段等更隐蔽的做法。',
        ],
      },
      {
        heading: '评价体系失真带来什么',
        paragraphs: [
          '当评分可以被购买，消费者做选择时依赖的信号就被污染了。真正用心经营、不愿刷单的商家反而因为分数不够高而失去曝光，形成劣币驱逐良币的局面。',
        ],
      },
      {
        heading: '作为消费者能看什么',
        paragraphs: [
          '与其只盯着总分，不如多翻几页评价，看看带图和写具体体验的内容。留意差评集中在哪些问题上，是口味、分量还是配送。',
          '发现明显异常的评价时，可以通过平台举报入口反馈，帮助维护整体环境。',
        ],
      },
    ],
    timeline: [
      { time: '曝光', event: '多方讨论外卖评价体系中存在的水分问题。' },
      { time: '回应', event: '平台表示将升级风控模型，加大对异常评价的识别力度。' },
      { time: '持续追踪', event: '评价治理效果有待长期观察。' },
    ],
  },
  {
    slug: 'medical-beauty-marketing',
    title: '医美营销里的"效果承诺"：不能说的保证从何而来',
    category: '行业内幕',
    summary:
      '医美属于医疗行为，本不应承诺确定效果。但在营销话术中，绝对化的表述仍然常见。本文梳理了其中的边界与风险提示。',
    tags: ['医美', '营销合规', '风险提示'],
    date: '2026-08-08',
    readTime: 8,
    heat: 7240,
    cover:
      'https://readdy.ai/api/search-image?query=A%20clean%20minimalist%20cosmetic%20clinic%20treatment%20room%20with%20soft%20neutral%20decor%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-06&orientation=landscape',
    keyPoints: [
      '医疗美容属于医疗行为，应以医生面诊与个体评估为前提。',
      '"包效果""无风险"之类的绝对化承诺，本身就值得警惕。',
      '确认机构与人员的资质，是决定是否接受服务前的必要步骤。',
    ],
    sections: [
      {
        heading: '效果承诺为什么不该存在',
        paragraphs: [
          '医疗行为的结果受到个体差异、术后护理等多种因素影响，任何负责任的医生都不会承诺确定效果。因此，当营销中出现"一次见效""永久维持"这类表述时，本身就是危险信号。',
          '这类话术利用的是消费者对确定性的期待，把复杂的医疗决策简化成一次消费选择。',
        ],
      },
      {
        heading: '到店前后要注意什么',
        paragraphs: [
          '到店前，先核实机构的执业许可与操作人员的资质，可以通过官方渠道查询。到店后，坚持要求医生面诊，而不是由咨询师直接给出方案。',
          '对于"今天下单立减""名额只剩一个"之类的催促，不必回应，决策节奏应该由自己掌握。',
        ],
      },
      {
        heading: '出现问题后如何固定证据',
        paragraphs: [
          '保留术前沟通记录、收费凭证、术前术后照片以及病历资料。一旦出现纠纷，这些材料是厘清责任的关键。',
          '遇到严重问题，应及时就医并保留诊疗记录，再通过正规渠道主张权益。',
        ],
      },
    ],
    timeline: [
      { time: '近期', event: '部分地区加强对医美广告与营销话术的规范。' },
      { time: '讨论', event: '关于效果承诺边界的话题持续引发关注。' },
      { time: '持续追踪', event: '行业规范与消费者教育仍在推进。' },
    ],
  },
  {
    slug: 'used-car-mileage',
    title: '二手车"调表"疑云：里程数为什么总是刚好',
    category: '争议事件',
    summary:
      '里程数是二手车定价的重要变量，也因此成为被动手脚的高发环节。我们梳理了识别里程异常的几种思路。',
    tags: ['二手车', '里程造假', '验车'],
    date: '2026-08-04',
    readTime: 7,
    heat: 5870,
    cover:
      'https://readdy.ai/api/search-image?query=A%20used%20silver%20sedan%20car%20dashboard%20and%20steering%20wheel%20close%20up%20in%20daylight%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-07&orientation=landscape',
    keyPoints: [
      '里程异常可以通过保养记录、轮胎磨损与内饰使用痕迹交叉验证。',
      '购车前委托第三方检测，能显著降低信息不对称。',
      '合同中应写明里程数承诺及违约责任，便于后续主张。',
    ],
    sections: [
      {
        heading: '里程数为何成为争议焦点',
        paragraphs: [
          '在二手车定价里，行驶里程直接关系到车辆剩余寿命与维修成本。里程越低，通常报价越高。正因为如此，调整里程表成了一个有利可图的操作。',
          '难点在于，单看仪表盘很难判断真伪，需要结合多方面的线索。',
        ],
      },
      {
        heading: '几个交叉验证的线索',
        paragraphs: [
          '第一是保养记录。正规保养会在系统中留痕，里程通常呈递增趋势，出现回跳就有问题。第二是易损件。刹车盘、轮胎的磨损程度若与里程明显不符，值得追问。',
          '第三是内饰细节。方向盘、座椅与踏板的磨损，往往比表显里程更诚实。',
        ],
      },
      {
        heading: '交易时如何保护自己',
        paragraphs: [
          '尽量选择提供检测报告的渠道，并自行复核。签署买卖合同时，要求明确写入里程数，并约定如与实车不符的处理方式。',
          '保留所有交易凭证与聊天记录，一旦发现问题，维权会更有底气。',
        ],
      },
    ],
    timeline: [
      { time: '个案', event: '消费者反映购得车辆里程与保养记录不符。' },
      { time: '争议', event: '买卖双方就赔偿责任产生分歧。' },
      { time: '持续追踪', event: '相关个案依合同与检测结论处理中。' },
    ],
  },
  {
    slug: 'big-data-price-difference',
    title: '"同单不同价"是怎么回事：关于差别定价的讨论',
    category: '争议事件',
    summary:
      '同一件商品，不同账号看到的价格可能不同。这种现象引发了关于算法定价的广泛讨论，我们整理了目前主流的几种解释。',
    tags: ['算法定价', '消费者权益', '争议'],
    date: '2026-07-30',
    readTime: 6,
    heat: 5460,
    cover:
      'https://readdy.ai/api/search-image?query=A%20person%20using%20a%20smartphone%20with%20abstract%20price%20charts%20on%20a%20cafe%20table%2C%20warm%20muted%20tones%2C%20conceptual%20editorial%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-08&orientation=landscape',
    keyPoints: [
      '差异化展示可能来自优惠券、会员等级等公开规则，也可能来自算法排序。',
      '判断是否构成不当定价，需要区分正常促销与针对个体的价格差异。',
      '遇到疑似情况，截图对比并保留记录，是后续反映问题的基础。',
    ],
    sections: [
      {
        heading: '价格为什么会有差异',
        paragraphs: [
          '价格差异本身并不罕见。新客立减、会员专享、限时活动都会导致不同用户看到不同的到手价，这些属于公开的促销规则。',
          '引发争议的，是那些难以解释、也难以复现的差异。当两个条件相近的账号看到同一商品的不同价格，且平台无法给出合理解释时，消费者的疑虑就会被放大。',
        ],
      },
      {
        heading: '如何分辨与应对',
        paragraphs: [
          '先排除优惠券与会员因素，再看差异是否稳定复现。如果多次测试都指向同一规律，可以截图记录并对比时间、账号与商品链接。',
          '向平台客服反映时，尽量以具体证据说明问题，而不是仅凭印象。',
        ],
      },
      {
        heading: '监管与行业的方向',
        paragraphs: [
          '围绕算法定价的讨论，核心在于透明与可解释。让消费者知道价格如何形成，是建立信任的前提。',
        ],
      },
    ],
    timeline: [
      { time: '讨论起', event: '网络平台出现关于"同单不同价"的讨论。' },
      { time: '回应', event: '平台方面表示价格差异与优惠规则有关。' },
      { time: '持续追踪', event: '相关话题的监管讨论仍在进行。' },
    ],
  },
  {
    slug: 'renovation-extra-cost',
    title: '装修"低价签单、后期加项"：报价单里的隐藏变量',
    category: '行业内幕',
    summary:
      '装修预算常常在施工中途被打破。我们拆解了加项从何而来，以及如何在签约阶段把不确定性降到更低。',
    tags: ['装修', '报价陷阱', '合同'],
    date: '2026-07-26',
    readTime: 8,
    heat: 5230,
    cover:
      'https://readdy.ai/api/search-image?query=A%20half%20renovated%20apartment%20interior%20with%20paint%20buckets%20and%20a%20ladder%2C%20warm%20muted%20tones%2C%20documentary%20editorial%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-09&orientation=landscape',
    keyPoints: [
      '低价签单往往依靠漏项实现，签约后再以"必须做"为由追加。',
      '要求在合同中附上相对完整的材料与工艺清单，可减少扯皮空间。',
      '每笔变更都保留书面确认，避免结算时各说各话。',
    ],
    sections: [
      {
        heading: '加项是怎么发生的',
        paragraphs: [
          '常见的操作是先以明显低于市场价的报价签单，报价单里故意省略防水、找平、拆改等项目。开工后再告知这些是必要工序，业主此时进退两难。',
          '另一种是材料替换。合同只写品牌不写型号，施工时用低一档的产品替代，差价就进了承包方口袋。',
        ],
      },
      {
        heading: '签约阶段能锁定的东西',
        paragraphs: [
          '报价单越细越好，最好精确到工序、材料品牌与型号、施工面积。对于可能增加的项目，提前约定单价上浮范围。',
          '付款节奏也应与工程节点挂钩，避免前期就支付大部分款项。',
        ],
      },
      {
        heading: '施工中的留痕习惯',
        paragraphs: [
          '任何变更都用文字确认，哪怕是聊天记录。保留材料进场时的照片与票据，结算时逐项核对。',
        ],
      },
    ],
    timeline: [
      { time: '初次曝出', event: '业主反映施工中被要求追加多项费用。' },
      { time: '协商', event: '双方就漏项责任与费用分摊沟通。' },
      { time: '持续追踪', event: '相关合同规范话题受到关注。' },
    ],
  },
  {
    slug: 'parcel-insurance-claim',
    title: '快递"保价"理赔争议：为什么赔的比保的少',
    category: '维权纠纷',
    summary:
      '寄件时买了保价，出问题后却只拿到部分赔偿。围绕保价金额与实际赔付的差距，争议一直存在。',
    tags: ['快递', '保价', '理赔'],
    date: '2026-07-22',
    readTime: 6,
    heat: 4980,
    cover:
      'https://readdy.ai/api/search-image?query=Cardboard%20parcels%20stacked%20at%20a%20sorting%20counter%20with%20a%20delivery%20clipboard%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-10&orientation=landscape',
    keyPoints: [
      '保价条款中的赔付比例与举证要求，是争议的核心。',
      '寄送贵重物品前，拍照记录物品状态与价值凭证。',
      '理赔受阻时，可通过邮政管理部门渠道反映。',
    ],
    sections: [
      {
        heading: '保价与赔付之间的落差',
        paragraphs: [
          '很多人在寄件时按提示选择了保价，默认出了问题就能按保价金额获赔。但实际操作中，赔付还可能受到条款约定、价值证明等因素影响。',
          '争议往往出现在"如何证明物品价值"这一环。如果寄件人无法提供有效凭证，赔付金额就可能被压低。',
        ],
      },
      {
        heading: '寄件前的准备',
        paragraphs: [
          '对贵重物品，先拍照或录像记录外观与状态，保留购买发票或交易记录。填写保价金额时，与实际价值相符更利于后续主张。',
          '选择服务时，了解清楚该渠道的赔付规则，而不是仅凭保价选项的字面理解。',
        ],
      },
      {
        heading: '理赔不顺怎么办',
        paragraphs: [
          '先与快递企业协商，保留工单号与沟通记录。协商未果，可向邮政管理部门或消费者组织反映。',
        ],
      },
    ],
    timeline: [
      { time: '寄件', event: '消费者为贵重物品办理保价寄送。' },
      { time: '出险', event: '包裹损坏，双方就赔付金额产生分歧。' },
      { time: '持续追踪', event: '个案通过协商与投诉渠道继续处理。' },
    ],
  },
  {
    slug: 'agency-hidden-contract',
    title: '房产中介的"阴阳合同"风险：为什么不能图省事',
    category: '争议事件',
    summary:
      '为了少缴税费而签两份价格不同的合同，看似双方都得利，实则埋下隐患。我们来梳理其中的风险链条。',
    tags: ['房产中介', '合同风险', '税费'],
    date: '2026-07-18',
    readTime: 7,
    heat: 4760,
    cover:
      'https://readdy.ai/api/search-image?query=A%20real%20estate%20agent%20desk%20with%20a%20miniature%20house%20model%20and%20contract%20papers%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-11&orientation=landscape',
    keyPoints: [
      '价款不一致的两份合同，会直接冲击合同效力的认定。',
      '一旦发生纠纷，申报价较低的一方往往处于不利位置。',
      '购房交易应坚持一份真实、完整的合同与对价。',
    ],
    sections: [
      {
        heading: '看似双赢，其实风险单边',
        paragraphs: [
          '"阴阳合同"通常指买卖双方签订两份价格不同的合同，一份登记使用，一份私下执行。表面上看，卖方少缴了税，买方也降低了成本。',
          '但风险并不对称。一旦房屋出现权属问题或交易生变，买方主张损失时，能依据的往往是那份价格较低的合同，实际支付的部分难以获得同等保护。',
        ],
      },
      {
        heading: '纠纷常见形态',
        paragraphs: [
          '一种情形是卖方反悔，主张按备案合同价格履行。另一种是出现税务或权属核查时，双方都需要解释价款差异。',
          '无论哪种，都会让交易成本与不确定性大幅上升。',
        ],
      },
      {
        heading: '更稳妥的做法',
        paragraphs: [
          '坚持单一合同、真实对价。所有款项走可追溯的支付渠道，并约定违约责任。中介的建议可以听，但最终要为合同签字的是自己。',
        ],
      },
    ],
    timeline: [
      { time: '交易', event: '买卖双方在中介撮合下签署价格不一致的合同。' },
      { time: '生变', event: '交易过程中一方反悔，纠纷显现。' },
      { time: '持续追踪', event: '个案依真实交易情况与法律认定处理。' },
    ],
  },
  {
    slug: 'elderly-health-products',
    title: '会销现场的话术拆解：为什么长辈容易在现场下单',
    category: '消费乱象',
    summary:
      '以健康讲座为名的营销活动，往往在熟人氛围中完成转化。理解它的话术结构，是帮助家人规避风险的第一步。',
    tags: ['老年消费', '会销', '消费提示'],
    date: '2026-07-14',
    readTime: 7,
    heat: 6120,
    cover:
      'https://readdy.ai/api/search-image?query=A%20community%20hall%20with%20rows%20of%20empty%20chairs%20and%20a%20small%20stage%2C%20warm%20muted%20tones%2C%20documentary%20editorial%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1200&height=800&seq=rc-topic-12&orientation=landscape',
    keyPoints: [
      '会销依靠赠品、免费体验与熟人陪同降低戒备心。',
      '现场制造的从众氛围，会明显压缩理性思考的时间。',
      '与长辈保持沟通、共同查看产品资质，比事后劝阻更有效。',
    ],
    sections: [
      {
        heading: '话术是如何一步步推进的',
        paragraphs: [
          '典型流程是先用免费礼品把老人请到现场，再通过健康讲座建立专业感，接着请"老顾客"现身说法，最后在限定时间内推出优惠。',
          '整个过程环环相扣，每一步都在降低参与者的心理防线。等到了下单环节，拒绝的成本已经被抬得很高。',
        ],
      },
      {
        heading: '为什么理性在现场容易失效',
        paragraphs: [
          '现场往往坐满了同龄人，从众效应会让个体更倾向于跟随多数。加上主持人不断的正面反馈，老人会对自己的判断产生更强的确信。',
          '此外，赠品与"专属名额"制造了一种亏欠感与稀缺感，进一步压缩了思考空间。',
        ],
      },
      {
        heading: '家人可以做什么',
        paragraphs: [
          '与其在事后责备，不如平时多聊这些套路，提前打个预防针。遇到大额消费，建议陪同前往，一起核对产品资质与价格。',
          '帮助长辈建立"大额支出先缓一缓"的习惯，往往比任何劝阻都管用。',
        ],
      },
    ],
    timeline: [
      { time: '活动', event: '以健康讲座为名的营销活动在部分社区出现。' },
      { time: '反映', event: '家属就产品宣传与价格问题提出质疑。' },
      { time: '持续追踪', event: '相关消费提示持续发布。' },
    ],
  },
];

export const faqs = [
  {
    q: '锐察社的内容是如何选题的？',
    a: '我们围绕消费者日常容易遇到的场景做选题，比如退费、预付、评价与合同纠纷，并尽量给出可操作的处理思路，而不是只做情绪表达。',
  },
  {
    q: '站内的个案是否指名了具体企业？',
    a: '我们以现象和议题为单位进行盘点，关注行为模式本身，不针对未核实的具体主体作指控，也欢迎当事方提供补充信息。',
  },
  {
    q: '遇到类似问题，第一步应该做什么？',
    a: '先固定证据：保留合同、付款凭证、沟通记录与现场照片。证据越完整，后续无论是协商、投诉还是诉讼，都会更有依据。',
  },
  {
    q: '如何向锐察社提供线索或反馈？',
    a: '可以通过页面底部的联系方式与我们取得联系，说明议题背景与已有材料，我们会据此判断是否适合做进一步梳理。',
  },
  {
    q: '站内内容可以直接作为法律依据吗？',
    a: '不能。站内内容用于帮助理解议题脉络，不构成法律意见。涉及具体纠纷时，建议咨询专业人士并结合个案判断。',
  },
  {
    q: '议题会持续更新吗？',
    a: '会。对仍在发展中的议题，我们会在详情页以时间线的方式补充后续进展，方便读者看到完整脉络。',
  },
];

export const stats = [
  { label: '已梳理议题', value: '320+' },
  { label: '分类追踪方向', value: '4' },
  { label: '累计阅读', value: '86万' },
  { label: '读者反馈', value: '1.2万+' },
];